import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { CalendarIcon, MapPinIcon } from "../components/icons.jsx";
import { API } from "../config/api.js";
import { get, post } from "../lib/http.js";
import { format } from "date-fns";
import FancyLoader from "../components/FancyLoader.jsx";
import { toast } from "react-toastify";
import { readBbUserContact, toTenDigitMobile } from "../utils/phone.js";
import { fetchUserProfile } from "../lib/userProfile.js";
import { openRazorpayCheckout } from "../lib/razorpayCheckout.js";

const formatINR = (n) =>
  typeof n === "number" ? `₹${n.toLocaleString("en-IN")}` : "—";

// ✅ IST helper
const toISTDate = (input) => {
  if (!input) return null;
  if (input instanceof Date) return input;
  const s = String(input);
  const fixed = /Z$/i.test(s) ? s.replace(/Z$/i, "+05:30") : s;
  return new Date(fixed);
};

// ✅ Strict Exhibition check
const isExhibitionStrict = (ev) => String(ev?.category || "") === "Exhibition";

// ✅ Simple validators
const isNonEmpty = (s) => String(s || "").trim().length > 0;
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());
const isPhone = (s) => /^[0-9]{10,15}$/.test(String(s || "").trim().replace(/\D/g, ""));

export default function CheckoutPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [search] = useSearchParams();

  // Core states
  const [event, setEvent] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [qty, setQty] = useState(1);
  const [payoutLoading, setPayoutLoading] = useState(false);

  // small fix: user loading state referenced previously
  const [userLoading, setUserLoading] = useState(false);

  // Coupons
  const [discounts, setDiscounts] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  // Coupon popup state (new)
  const [showCouponPopup, setShowCouponPopup] = useState(false);
  const [couponPopupInfo, setCouponPopupInfo] = useState({
    code: "",
    discountAmount: 0,
    finalAmount: 0,
    cartAmount: 0,
  });

  // T&C modal state
  const [tncOpen, setTncOpen] = useState(false);
  const [tncContent, setTncContent] = useState("");
  const [tncTitle, setTncTitle] = useState("Terms & Conditions");

  // View-all toggle
  const [showAllCoupons, setShowAllCoupons] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const [showPopupSuccess, setShowPopupSuccess] = useState(false);
  const [popupBooking, setPopupBooking] = useState(null);

  const debounceRef = useRef(null);
  /** One Razorpay link per cart snapshot (tier / qty / coupon / server total). */
  const payoutIdempotencyRef = useRef({ sig: "", key: "" });

  // ✅ Attendees for Exhibition tickets only (includes companyName & designation)
  const [attendees, setAttendees] = useState([
    { name: "", email: "", phone: "", companyName: "", designation: "" },
  ]);

  // Derived Exhibition flag (strict)
  const exhibition = isExhibitionStrict(event);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [details, setDetails] = useState(() => {
    const c = readBbUserContact();
    return { name: c.name, email: c.email, mobile: c.mobile };
  });
  const [modalSubmitting, setModalSubmitting] = useState(false);

  // ---------------------
  // Important: auto-close effect must be declared BEFORE any early return so hooks order stays stable
  useEffect(() => {
    if (!showCouponPopup) return;
    const t = setTimeout(() => {
      setShowCouponPopup(false);
    }, 3500);
    return () => clearTimeout(t);
  }, [showCouponPopup]);
  // ---------------------

  useEffect(() => {
    (async () => {
      const session = readBbUserContact();
      setDetails((d) => ({
        name: d.name || session.name,
        email: d.email || session.email,
        mobile: d.mobile || session.mobile,
      }));

      try {
        setUserLoading(true);
        if (!session.token || !session.userId) return;
        const res = await fetchUserProfile(session.userId, session.token);
        if (!res) return;
        const mobile = toTenDigitMobile(res.mobile || res.phone) || session.mobile;
        setDetails({
          name: res?.name || session.name || "",
          email: res?.email || session.email || "",
          mobile,
        });
      } catch {
        if (!session.mobile) {
          toast.error("Failed to load profile.");
        }
      } finally {
        setUserLoading(false);
      }
    })();
  }, []);

  // Keep attendees length in sync with qty — only for Exhibition
  useEffect(() => {
    if (!exhibition) return;
    setAttendees((prev) => {
      const next = [...prev];
      if (qty > next.length) {
        return next.concat(
          Array.from({ length: qty - next.length }, () => ({
            name: "",
            email: "",
            phone: "",
            companyName: "",
            designation: "",
          }))
        );
      } else if (qty < next.length) {
        return next.slice(0, qty);
      }
      return next;
    });
  }, [qty, exhibition]);

  // All attendees valid? — Exhibition requires ALL fields
  const allAttendeesValid = useMemo(() => {
    if (!exhibition) return true;
    if (!Array.isArray(attendees) || attendees.length !== qty) return false;
    return attendees.every(
      (a) =>
        isNonEmpty(a.name) &&
        isEmail(a.email) &&
        isPhone(a.phone) &&
        isNonEmpty(a.companyName) &&
        isNonEmpty(a.designation)
    );
  }, [attendees, qty, exhibition]);

  const hardcodedTnc = `
🔓 How to Use:
Tap “Copy Code”
Go to Showtime Bro
Select your Ticket
Apply the code at checkout
Enjoy your discount!

📌 Terms:
Valid only on Showtime Bro
One-time use only
Coupon valid for a limited period only
Showtime Bro reserves the right to modify or cancel the offer without prior notice

`;

  useEffect(() => {
    if (window.fbq) {
      window.fbq("track", "InitiateCheckout");
    }
  }, []);

  // Price details from server (safe defaults)
  const [priceDetails, setPriceDetails] = useState({
    qty: 0,
    ticketPrice: 0,
    cartAmount: 0,
    convenienceFee: 0,
    gstRate: 0.18,
    gstAmount: 0,
    finalAmount: 0,
    discountApplied: false,
    discountAmount: 0,
    adjustedTicketCategories: [],
  });

  // ✅ Seating layout modal state
  const [layoutOpen, setLayoutOpen] = useState(false);

  // ✅ Resolve a layout image
  const layoutSrc = useMemo(() => {
    if (!event) return "";
    const candidates = [
      event?.layoutImage,
      event?.layoutUrl,
      event?.venue?.layoutImage,
      event?.venue?.layoutUrl,
      Array.isArray(event?.landsacpImage)
        ? event.landsacpImage.find(
          (u) => typeof u === "string" && /layout|seat|map|plan/i.test(u)
        ) || event.landsacpImage[0]
        : null,
    ].filter(Boolean);
    return candidates[0] || "";
  }, [event]);

  /* qty change par coupon reset + reprice */
  function clearCouponAndReprice(newQty) {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
    if (selectedTier) updatePriceDetails(selectedTier, newQty, undefined);
  }

  /* Load coupons for this event */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
        const res = await get(`${API.coupon.getDiscount}?eventId=${id}`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });
        if (!alive) return;
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.discounts)
            ? res.discounts
            : Array.isArray(res?.discount)
              ? res.discount
              : Array.isArray(res?.data?.discounts)
                ? res.data.discounts
                : [];
        setDiscounts(list);
      } catch (e) {
        console.error("load discounts error:", e);
        setDiscounts([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  /* Load event & tickets */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
        const res = await get(`${API.events.getSinglEvent}/${id}`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });
        if (!alive) return;

        const ev = res?.event;
        setEvent(ev || null);

        const mapped = (ev?.ticketCategories || []).map((t, i) => ({
          key: t.key ?? t.type ?? String(i),
          tier: t.type ?? t.key ?? `Tier ${i + 1}`,
          desc: t.desc || "Ticket",
          perks: t.perks,
          price: Number(t.price || 0),
          fee: Number(t.fee || 0),
          isTicketAvailable: t.isTicketAvailable,
          availableSeats: Number(t.availableSeats ?? 0),
          tone:
            Number(t.availableSeats ?? 0) === 0
              ? "red"
              : Number(t.availableSeats ?? 0) < 20
                ? "amber"
                : "green",
          badge:
            Number(t.availableSeats ?? 0) === 0
              ? "Sold Out"
              : Number(t.availableSeats ?? 0) < 20
                ? `Only ${Number(t.availableSeats)} left`
                : "Available",
        }));
        setTickets(mapped);

        // Preselect from query ?tier=
        const qKey = search.get("tier");
        let pre = null;
        if (qKey) {
          pre =
            mapped.find((t) => t.key === qKey) ||
            mapped.find((t) => t.tier === qKey);
        }
        if (!pre) pre = mapped.find((t) => t.availableSeats > 0) || mapped[0] || null;
        setSelectedTier(pre || null);
      } catch (e) {
        console.error("checkout fetch event error:", e);
        setEvent(null);
        setTickets([]);
        setSelectedTier(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, search]);

  /* Server pricing refresh (selectedTier/qty/coupon) */
  useEffect(() => {
    let alive = true;
    if (!selectedTier) return;

    (async () => {
      try {
        const tierId = selectedTier.key || selectedTier.tier;
        const expanded = Array.from({ length: qty }, () => ({
          categoryId: tierId,
          quantity: 1,
        }));

        const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
        const userId = JSON.parse(localStorage.getItem("bb_user"))?.id;

        const payload = {
          userId,
          eventId: id,
          ticketCategories: expanded,
          ...(appliedCoupon?.code ? { discountCode: appliedCoupon.code } : {}),
        };

        const res = await post(`${API.coupon.validDescount}`, payload, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });

        if (!alive) return;
        const pricePayload = res?.data?.priceDetails || res?.data || res;
        if (pricePayload) setPriceDetails((p) => ({ ...p, ...pricePayload }));
      } catch (e) {
        console.error("load pricing error:", e);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id, selectedTier, qty, appliedCoupon?.code]);

  /* Coupon helpers */
  const now = Date.now();
  const isCouponApplicableToEvent = (c) => {
    if (!Array.isArray(c.applicableEvents) || c.applicableEvents.length === 0) return true;
    return c.applicableEvents.includes(event?._id);
  };
  const isWithinValidity = (c) => {
    const vf = c.validFrom ? new Date(c.validFrom).getTime() : null;
    const vt = c.validTo ? new Date(c.validTo).getTime() : null;
    if (vf && now < vf) return false;
    if (vt && now > vt) return false;
    return true;
  };
  const hasUsageLeft = (c) => {
    if (!c.usageLimit || c.usageLimit <= 0) return true;
    const used = Number(c.usedCount ?? 0);
    return used < c.usageLimit;
  };
  const meetsMinPurchase = (c) => {
    const min = Number(c.minPurchaseAmount ?? 0);
    return priceDetails.cartAmount >= min;
  };

  // Input code -> matching discount (for T&C if needed later)
  const matchedDiscountForInput = useMemo(() => {
    const code = String(couponCode || "").trim().toLowerCase();
    if (!code) return null;
    return discounts.find((d) => String(d.code || "").toLowerCase() === code) || null;
  }, [couponCode, discounts]);

  // Open T&C (coupon or event)
  function openTncFor(coupon, titleSuffix = "") {
    const raw = coupon?.termsAndConditions ?? event?.termsAndConditions ?? "";
    let content = "";
    let items = null;

    if (Array.isArray(raw)) {
      items = raw.map((s) => (typeof s === "string" ? s.trim() : "")).filter(Boolean);
    } else if (typeof raw === "string") {
      const trimmed = raw.trim();
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          items = parsed.map((s) => (typeof s === "string" ? s.trim() : "")).filter(Boolean);
        } else {
          content = trimmed;
        }
      } catch {
        if (/<[^>]+>/.test(trimmed)) {
          content = trimmed;
        } else {
          const split = trimmed
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean);
          items = split.length ? split : null;
          if (!items) content = trimmed;
        }
      }
    }

    setTncContent(items?.length ? items : content || "No terms & conditions available.");
    setTncTitle(`Terms & Conditions${titleSuffix ? ` — ${titleSuffix}` : ""}`);
    setTncOpen(true);
  }

  // Server pricing (single source of truth)
  async function updatePriceDetails(tier, quantity, appliedCode, opts = {}) {
    const { surfaceErrors = false } = opts;
    if (!tier) {
      if (surfaceErrors) setCouponError("Please select a ticket category first.");
      return false;
    }

    try {
      const user = JSON.parse(localStorage.getItem("bb_user") || "null");
      const token = user?.token;
      const userId = user?.id;
      const codeTrimmed = String(appliedCode ?? "").trim();

      const payload = {
        userId,
        eventId: id,
        ticketCategories: [{ categoryId: tier.key || tier.tier, quantity }],
        ...(codeTrimmed ? { discountCode: codeTrimmed } : {}),
      };

      const res = await post(`${API.coupon.validDescount}`, payload, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "application/json",
        },
      });

      const success = res?.success ?? res?.ok ?? true;
      const message =
        res?.message || res?.error || res?.data?.message || res?.data?.error || "";

      if (!success) {
        if (surfaceErrors) setCouponError(message || "Failed to apply coupon.");
        return false;
      }

      const pricePayload = res?.data?.priceDetails || res?.data || res;
      if (pricePayload) {
        setPriceDetails((p) => ({ ...p, ...pricePayload }));
        // Return the payload so caller can show popup info
        return pricePayload;
      }

      // If no payload, still return a generic true
      return true;
    } catch (e) {
      console.error("updatePriceDetails error:", e);
      const serverMsg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to apply coupon.";
      if (surfaceErrors) setCouponError(serverMsg);
      return false;
    }
  }

  async function handleSelectTier(t) {
    try {
      setSelectedTier(t);
      setQty(1);
      setAppliedCoupon(null);
      setCouponCode("");
      setCouponError("");

      const user = JSON.parse(localStorage.getItem("bb_user") || "null");
      const token = user?.token;

      const payload = {
        userId: user?.id,
        eventId: id,
        ticketCategories: [{ categoryId: t.key || t.tier, quantity: 1 }],
      };

      const res = await post(`${API.coupon.validDescount}`, payload, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "application/json",
        },
      });

      const pricePayload = res?.data?.priceDetails || res?.data || res;
      if (pricePayload) setPriceDetails((p) => ({ ...p, ...pricePayload }));
    } catch (e) {
      console.error("load discounts error:", e);
    }
  }

  // applyByCode (updated to show popup)
  const applyByCode = async (code) => {
    setCouponError("");
    const input = String(code ?? "").trim();
    if (!input) {
      setAppliedCoupon(null);
      setCouponCode("");
      await updatePriceDetails(selectedTier, qty, "", { surfaceErrors: false });
      return;
    }

    if (applyingCoupon) return;

    setApplyingCoupon(true);
    try {
      const pricePayload = await updatePriceDetails(selectedTier, qty, input, {
        surfaceErrors: true,
      });

      // pricePayload will be false on failure, or the payload object on success
      if (pricePayload) {
        setAppliedCoupon({ code: input });
        setCouponCode(input);
        setCouponError("");

        // prepare popup info - defensive fields
        const discountAmount =
          Number(pricePayload.discountAmount ?? pricePayload.discount ?? 0) || 0;
        const finalAmount =
          Number(pricePayload.finalAmount ?? pricePayload.total ?? 0) || 0;
        const cartAmount =
          Number(pricePayload.cartAmount ?? pricePayload.subtotal ?? 0) || 0;

        setCouponPopupInfo({
          code: input.toUpperCase(),
          discountAmount,
          finalAmount,
          cartAmount,
        });

        // Show animated popup
        setShowCouponPopup(true);
      } else {
        setAppliedCoupon(null);
      }
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = async () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
    setShowCouponPopup(false);
    setCouponPopupInfo({ code: "", discountAmount: 0, finalAmount: 0, cartAmount: 0 });
    if (selectedTier) {
      await updatePriceDetails(selectedTier, qty, undefined);
    }
  };

  const eligibleCoupons = useMemo(() => {
    return (discounts || []).filter(
      (c) =>
        c?.isActive !== false &&
        isCouponApplicableToEvent(c) &&
        isWithinValidity(c) &&
        hasUsageLeft(c)
    );
  }, [discounts, event?._id, now]);

  const displayCoupons = useMemo(() => eligibleCoupons, [eligibleCoupons]);

  // ORDER & SLICE COUPONS
  const orderedCoupons = useMemo(() => {
    const base = Array.isArray(displayCoupons) ? [...displayCoupons] : [];
    if (appliedCoupon?.code) {
      const idx = base.findIndex((c) => c.code === appliedCoupon.code);
      if (idx > -1) {
        const [sel] = base.splice(idx, 1);
        base.unshift(sel);
      }
    }
    return base;
  }, [displayCoupons, appliedCoupon?.code]);

  const SHOW_COUNT = 1;
  const couponsToRender = useMemo(
    () => (showAllCoupons ? orderedCoupons : orderedCoupons.slice(0, SHOW_COUNT)),
    [orderedCoupons, showAllCoupons]
  );

  const reasonNotEligible = (c) => {
    if (!isCouponApplicableToEvent(c)) return "Not for this event";
    if (!isWithinValidity(c)) return "Not in validity period";
    if (!hasUsageLeft(c)) return "Usage limit reached";
    if (!meetsMinPurchase(c)) return `Min. purchase ₹${Number(c.minPurchaseAmount || 0)}`;
    return "";
  };

  const clearCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
  };

  /* -------------------- Misc helpers -------------------- */
  if (!event || !selectedTier)
    return (
      <main className="w-full px-4 py-10 text-center justify-center">
        <FancyLoader label="Processing..." />
      </main>
    );

  // header
  const headerLocation = event?.venue
    ? `${event.venue.name || ""} ${event.venue.city || ""}`.trim()
    : "—";
  const headerDate = event?.date
    ? format(toISTDate(event.date), "EEE, dd MMM • h:mm a")
    : "—";

  // ✅ Exhibition: allow 1 or 2; Others: up to event.maxTicketsPerUser (default 6)
  const minQtyRequired = 1;
  const maxPerUser = Math.max(1, Number(event?.maxTicketsPerUser) || 6);
  const maxAllowedQty = exhibition ? 2 : maxPerUser;

  // ✅ payout payload
  const buildPayoutBody = () => {
    const user = JSON.parse(localStorage.getItem("bb_user")) || {};
    const userId =
      user?.user?._id || user?.user?.id || user?._id || user?.id || "";

    const tierKey = selectedTier?.key || selectedTier?.tier || "";
    const couponPart = appliedCoupon?.code || "";
    const totalRounded = Math.round(Number(priceDetails?.finalAmount ?? 0));
    const idemSig = `${String(event?._id || id || "")}|${tierKey}|${qty}|${couponPart}|${totalRounded}`;
    if (payoutIdempotencyRef.current.sig !== idemSig) {
      payoutIdempotencyRef.current = {
        sig: idemSig,
        key:
          typeof globalThis.crypto?.randomUUID === "function"
            ? globalThis.crypto.randomUUID()
            : `idem-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`,
      };
    }

    const base = {
      amount: Math.round(priceDetails.finalAmount),
      userId,
      eventId: event?._id,
      ticketCategories: [
        {
          categoryId: selectedTier?.key || selectedTier?.tier,
          quantity: qty,
          price: Number(selectedTier?.price || 0),
        },
      ],
      discountCode: appliedCoupon?.code || "",
      idempotencyKey: payoutIdempotencyRef.current.key,
    };

    // Only for Exhibition: send guestDetails (exact key as requested)
    if (exhibition) {
      base.gestDetails = attendees.map((a, i) => ({
        index: i + 1,
        name: String(a.name || "").trim(),
        email: String(a.email || "").trim(),
        phone: String(a.phone || "").trim(),
        companyName: String(a.companyName || "").trim(),
        designation: String(a.designation || "").trim(),
      }));
    }

    return base;
  };

  async function handlePayoutClick(userDetails) {
    try {
      if (!selectedTier) {
        alert("Please select a ticket category.");
        return;
      }
      if (exhibition && !allAttendeesValid) {
        toast.error("Please fill Name, Email, Phone, Company Name & Designation for all attendees.");
        return;
      }

      const session = readBbUserContact();
      const mobile =
        toTenDigitMobile(userDetails?.mobile) || session.mobile;
      if (!mobile) {
        toast.error("Mobile number missing. Please log in again with OTP.");
        return;
      }

      setPayoutLoading(true);
      let body = buildPayoutBody ? buildPayoutBody() : {};
      body.userDetails = {
        name: (userDetails?.name || session.name || "").trim(),
        email: (userDetails?.email || session.email || "").trim(),
        mobile,
      };
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const res = await post(API.payout.payout, body, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });

      if (res?.success && res?.checkout?.order_id) {
        toast.success("Opening secure payment…");

        const checkoutPayload = {
          ...res.checkout,
          prefill: {
            ...res.checkout.prefill,
            contact:
              res.checkout.prefill?.contact ||
              mobile ||
              session.mobile,
          },
        };

        await openRazorpayCheckout({
          checkout: checkoutPayload,
          onSuccess: async (paymentId) => {
            await post(
              API.payout.PaymentSuccessful,
              { razorpay_payment_id: paymentId },
              {
                headers: {
                  Authorization: token ? `Bearer ${token}` : undefined,
                  "Content-Type": "application/json",
                },
              }
            );
            nav(
              `/payment-successful?razorpay_payment_id=${encodeURIComponent(paymentId)}`
            );
          },
          onDismiss: () => {
            toast.info("Payment cancelled");
          },
        });
      } else if (res?.success && res?.payment_url) {
        window.location.href = res.payment_url;
      } else if (res?.success) {
        setPopupBooking(res?.booking || {});
        setShowPopupSuccess(true);
      } else {
        toast.error(res?.message || "Failed to create payment link");
      }
    } catch (err) {
      console.error("Payout API error:", err?.message || err);
      toast.error(err?.message || "Failed to initiate payout. Please try again.");
    } finally {
      setPayoutLoading(false);
    }
  }

  const handleClose = () => {
    setShowPopupSuccess(false);
    nav("/tickets");
  };

  // ✅ disable logic for button (Exhibition rules only)
  const bookDisabled =
    payoutLoading ||
    !selectedTier ||
    qty < minQtyRequired ||
    qty > (selectedTier?.availableSeats ?? 1) ||
    (exhibition && !allAttendeesValid);

  /* --------------------------------- UI --------------------------------- */
  return (
    <>
      {/* TOP */}
      <section className="w-full bg-[#fff8e1] border-b">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="flex items-center gap-3 min-w-0">
              <img
                src={event.imageUrl}
                alt={event.name}
                className="h-14 w-20 md:h-16 md:w-24 object-cover rounded-lg border shrink-0"
              />
              <div className="min-w-0">
                <div className="text-[10px] md:text-xs uppercase font-semibold text-amber-700">
                  Checkout
                </div>
                <h1 className="text-lg md:text-xl font-extrabold text-slate-900 truncate">
                  {event.name}
                </h1>
                <div className="mt-1 text-xs md:text-sm text-slate-700 flex flex-wrap gap-x-4 gap-y-1">
                  <span className="inline-flex items-center gap-1 min-w-0 max-w-full">
                    <MapPinIcon className="shrink-0" />
                    <span className="truncate">{headerLocation}</span>
                  </span>
                  <span className="inline-flex items-center gap-1 min-w-0 max-w-full">
                    <CalendarIcon className="shrink-0" />
                    <span className="truncate">{headerDate}</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-2 md:ml-auto">
              <button
                onClick={() => nav(-1)}
                className="rounded-full bg-white border px-3 py-1.5 text-sm font-semibold hover:bg-slate-50"
              >
                ← Back
              </button>

              {layoutSrc && (
                <button
                  type="button"
                  onClick={() => setLayoutOpen(true)}
                  className="rounded-full bg-white border px-3 py-1.5 text-sm font-semibold text-amber-700 hover:bg-amber-50"
                  title="View seating layout"
                >
                  <span className="md:hidden">Layout</span>
                  <span className="hidden md:inline">View seating layout</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* MAIN */}
      <main className="w-full pb-28 md:pb-0">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-6 grid gap-6 lg:grid-cols-3">
          {/* LEFT: ticket select + qty + attendees (for Exhibition) */}
          <div className="order-2 space-y-6 lg:order-1 lg:col-span-2">
            <div className="lg:sticky lg:top-4 space-y-6">
              <section className="rounded-2xl border bg-white shadow-sm">
                <div className="p-4 md:p-5 border-b">
                  <h2 className="font-semibold text-slate-900">Select Tickets</h2>
                </div>

                <div className="p-4 md:p-5 space-y-3">
                  {tickets
                    ?.filter((t) => t?.isTicketAvailable !== false)
                    .map((t, i) => {
                      const active = selectedTier?.key === t.key;
                      const avail = Number(t?.availableSeats ?? -1);
                      const soldOut = avail === 0;

                      return (
                        <div
                          key={t.key ?? t.tier ?? i}
                          className={`rounded-xl border p-4 ${active
                            ? "border-amber-400 ring-2 ring-amber-200 bg-white"
                            : "bg-white"
                            } ${soldOut ? "opacity-75" : ""}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <div className="font-semibold">{t.tier}</div>
                              </div>
                              <div className="text-sm text-slate-600">{t.desc}</div>
                              {Array.isArray(t.perks) &&
                                t.perks.filter((p) => p && p.trim() !== "").length > 0 && (
                                  <div className="mt-2 text-xs text-slate-700">
                                  
                                    {t.perks
                                      .filter((p) => p && p.trim() !== "")
                                      .join(", ")}
                                  </div>
                                )}
                            </div>

                            <div className="text-right shrink-0">
                              <div className="font-bold">{formatINR(t.price)}</div>

                              {soldOut ? (
                                <button
                                  className="mt-3 rounded-full bg-slate-200 px-4 py-1.5 text-sm font-semibold text-slate-500 cursor-not-allowed"
                                  disabled
                                >
                                  Sold out
                                </button>
                              ) : active ? (
                                <button
                                  className="mt-3 rounded-full bg-emerald-500 text-white px-4 py-1.5 text-sm font-semibold"
                                  onClick={() => setSelectedTier(t)}
                                >
                                  Selected ✓
                                </button>
                              ) : (
                                <button
                                  className="mt-3 rounded-full bg-amber-400 px-4 py-1.5 text-sm font-semibold text-slate-900 hover:bg-amber-300"
                                  onClick={() => {
                                    setSelectedTier(t);
                                    setQty(1);
                                    setAppliedCoupon(null);
                                    setCouponCode("");
                                    setCouponError("");
                                    updatePriceDetails(t, 1);
                                  }}
                                >
                                  Select
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                  {/* Quantity */}
                  <div className="mt-2 flex items-center justify-between rounded-xl border p-4">
                    <div className="text-sm font-medium text-slate-800">
                      Quantity
                      {exhibition && (
                        <span className="ml-2 text-xs text-amber-700 font-semibold">
                          (Exhibition: maximum 2)
                        </span>
                      )}
                    </div>
                    <div className="inline-flex items-center gap-3">
                      <button
                        className="h-8 w-8 grid place-items-center rounded-full border hover:bg-slate-50"
                        onClick={() => {
                          setQty((q) => {
                            const newQty = Math.max(1, q - 1);
                            clearCouponAndReprice(newQty);
                            return newQty;
                          });
                        }}
                      >
                        −
                      </button>

                      <div className="w-10 text-center font-semibold">{qty}</div>

                      <button
                        className="h-8 w-8 grid place-items-center rounded-full border hover:bg-slate-50 disabled:opacity-50"
                        onClick={() => {
                          setQty((q) => {
                            const newQty = Math.min(maxAllowedQty, q + 1);
                            clearCouponAndReprice(newQty);
                            return newQty;
                          });
                        }}
                        disabled={
                          (typeof selectedTier?.availableSeats === "number" &&
                            qty >= selectedTier.availableSeats) ||
                          qty >= maxAllowedQty
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* ✅ Attendee Details — only for Exhibition (ALL required) */}
                  {exhibition && (
                    <div className="mt-4 rounded-xl border p-4 space-y-4">
                      <div className="font-semibold text-slate-900">
                        Attendee Details ({qty})
                      </div>
                      {attendees.map((a, idx) => {
                        const nameErr = !isNonEmpty(a.name);
                        const emailErr = !isEmail(a.email);
                        const phoneDigits = String(a.phone || "").replace(/\D/g, "");
                        const phoneErr = !isPhone(phoneDigits);
                        const companyErr = !isNonEmpty(a.companyName);
                        const desigErr = !isNonEmpty(a.designation);
                        return (
                          <div
                            key={idx}
                            className="rounded-lg bg-slate-50 border p-3 space-y-3"
                          >
                            <div className="text-sm font-medium text-slate-800">
                              Attendee {idx + 1}
                            </div>

                            {/* Row 1: Name / Email / Phone */}
                            <div className="grid md:grid-cols-3 gap-3">
                              <input
                                type="text"
                                placeholder="Full Name"
                                value={a.name}
                                onChange={(e) =>
                                  setAttendees((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], name: e.target.value };
                                    return next;
                                  })
                                }
                                className={`border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${nameErr
                                  ? "border-red-300 focus:ring-red-300"
                                  : "focus:ring-amber-300"
                                  }`}
                              />
                              <input
                                type="email"
                                placeholder="Email"
                                value={a.email}
                                onChange={(e) =>
                                  setAttendees((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], email: e.target.value };
                                    return next;
                                  })
                                }
                                className={`border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${emailErr
                                  ? "border-red-300 focus:ring-red-300"
                                  : "focus:ring-amber-300"
                                  }`}
                              />
                              <input
                                type="tel"
                                inputMode="numeric"
                                placeholder="Phone"
                                value={a.phone}
                                onChange={(e) => {
                                  const digits = e.target.value.replace(/\D/g, "");
                                  setAttendees((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], phone: digits };
                                    return next;
                                  });
                                }}
                                className={`border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${phoneErr
                                  ? "border-red-300 focus:ring-red-300"
                                  : "focus:ring-amber-300"
                                  }`}
                              />
                            </div>

                            {/* Row 2: Company & Designation (REQUIRED) */}
                            <div className="grid md:grid-cols-2 gap-3">
                              <input
                                type="text"
                                placeholder="Company Name"
                                value={a.companyName}
                                onChange={(e) =>
                                  setAttendees((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], companyName: e.target.value };
                                    return next;
                                  })
                                }
                                className={`border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${companyErr
                                  ? "border-red-300 focus:ring-red-300"
                                  : "focus:ring-amber-300"
                                  }`}
                              />
                              <input
                                type="text"
                                placeholder="Designation"
                                value={a.designation}
                                onChange={(e) =>
                                  setAttendees((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], designation: e.target.value };
                                    return next;
                                  })
                                }
                                className={`border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${desigErr
                                  ? "border-red-300 focus:ring-red-300"
                                  : "focus:ring-amber-300"
                                  }`}
                              />
                            </div>

                            {(
                              nameErr ||
                              emailErr ||
                              phoneErr ||
                              companyErr ||
                              desigErr
                            ) && (
                                <p className="text-xs text-red-600">
                                  Please enter valid Name, Email, Phone, Company Name and Designation.
                                </p>
                              )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>

          {/* RIGHT: offers + summary */}
          <aside className="order-1 lg:order-2 lg:col-span-1">
            <div className="lg:sticky lg:top-4 space-y-4">
              {/* AVAILABLE OFFERS */}
              <div className="rounded-2xl border bg-white shadow-sm">
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">Available Offers</h3>
                  {appliedCoupon && (
                    <button
                      onClick={clearCoupon}
                      className="text-sm text-amber-700 hover:underline"
                    >
                      Remove coupon
                    </button>
                  )}
                </div>

                <div className="p-4">
                  {/* Apply input */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase());
                        setCouponError("");
                      }}
                      placeholder="Enter coupon code"
                      className="flex-1 border rounded-xl px-3 py-2 w-[105px] text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                      disabled={applyingCoupon}
                    />
                    <button
                      onClick={() => applyByCode(couponCode)}
                      className="rounded-full bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-amber-300"
                    >
                      Apply
                    </button>

                    {appliedCoupon && (
                      <button
                        onClick={removeCoupon}
                        className="ml-2 rounded-full bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-300"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  {/* T&C link */}
                  <div className="mt-2 relative">
                    <button
                      type="button"
                      onClick={() => setShowPopup(true)}
                      className="text-xs underline hover:text-slate-800 text-slate-600"
                    >
                      Terms &amp; Conditions
                    </button>

                    {showPopup && (
                      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white max-w-md w-[90%] p-5 rounded-xl shadow-lg relative">
                          <h2 className="text-lg font-semibold mb-2 text-amber-700">
                            Terms & Conditions
                          </h2>
                          <pre className="whitespace-pre-wrap text-sm text-slate-700">
                            {hardcodedTnc}
                          </pre>
                          <button
                            onClick={() => setShowPopup(false)}
                            className="absolute top-2 right-3 text-slate-500 hover:text-slate-800 text-lg"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {couponError && (
                    <p className="mt-2 text-sm text-red-600">{couponError}</p>
                  )}

                  {/* Coupon list */}
                  <div className="mt-4 grid grid-cols-1 gap-3">
                    {couponsToRender
                      .filter((c) => c.isShow)
                      .map((c) => {
                        const notOkReason = reasonNotEligible(c);
                        const disabled = !!notOkReason;
                        const isApplied = appliedCoupon?.code === c.code;

                        return (
                          <div
                            key={c._id}
                            className={`rounded-lg border p-3 ${isApplied ? "ring-2 ring-emerald-300 border-emerald-400" : ""
                              } ${disabled ? "opacity-60" : ""}`}
                          >
                            <div className="flex items-start gap-2">
                              <div className="text-xs font-mono px-2 py-1 rounded bg-slate-900 text-white">
                                {c.code}
                              </div>

                              <div className="min-w-0 w-[160px]">
                                <div className="text-sm font-semibold text-slate-900">
                                  {c.discountType === "percentage"
                                    ? `${Number(c.value || 0)}% OFF`
                                    : `₹${Number(c.value || 0)} OFF`}
                                </div>

                                {c.description && (
                                  <div className="text-xs text-slate-600 line-clamp-2">
                                    {c.description}
                                  </div>
                                )}

                                <div className="text-[11px] text-slate-500 mt-1">
                                  Min ₹{Number(c.minPurchaseAmount || 0)} • Valid{" "}
                                  {c.validFrom ? format(new Date(c.validFrom), "dd MMM") : "—"} –{" "}
                                  {c.validTo ? format(new Date(c.validTo), "dd MMM") : "—"}
                                </div>

                                {c.termsAndConditions && (
                                  <div className="mt-2">
                                    <button
                                      type="button"
                                      onClick={() => openTncFor(c, c.code ? `Code: ${c.code}` : "")}
                                      className="text-[11px] underline text-slate-600 hover:text-slate-800"
                                    >
                                      Terms &amp; Conditions
                                    </button>
                                  </div>
                                )}
                              </div>

                              <button
                                disabled={disabled}
                                onClick={() => applyByCode(c.code)}
                                className={`ml-auto rounded-full px-3 py-1.5 text-xs font-semibold ${disabled
                                  ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                                  : "bg-emerald-500 text-white hover:bg-emerald-400"
                                  }`}
                              >
                                {isApplied ? "Applied ✓" : "Apply"}
                              </button>
                            </div>

                            {disabled && (
                              <div className="mt-2 text-[11px] text-red-600">
                                {notOkReason}
                              </div>
                            )}
                          </div>
                        );
                      })}

                    {orderedCoupons.length === 0 && (
                      <div className="text-sm text-slate-500">
                        No offers available for this event right now.
                      </div>
                    )}

                    {orderedCoupons.length > SHOW_COUNT && (
                      <div className="mt-3 flex">
                        <button
                          type="button"
                          onClick={() => setShowAllCoupons((v) => !v)}
                          className="ml-auto text-md font-semibold text-amber-700 hover:underline"
                        >
                          {showAllCoupons ? "Show less" : `View all (${orderedCoupons.length})`}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ORDER SUMMARY */}
              <div className="rounded-2xl border bg-white shadow-sm">
                <div className="p-4 border-b">
                  <div className="text-sm text-slate-600">Order Summary</div>
                  <div className="mt-1 font-semibold text-slate-900">
                    {selectedTier.tier} × {qty}
                  </div>
                  {exhibition && (
                    <p className="mt-1 text-xs text-amber-700">
                      Exhibition: maximum 2 Tickets per order.
                    </p>
                  )}
                  {exhibition && !allAttendeesValid && (
                    <p className="mt-1 text-xs text-red-600">
                      Please fill valid details for all attendees.
                    </p>
                  )}
                </div>
                <div className="p-4 space-y-2 text-sm">
                  <Row
                    label={`Tickets (${qty} × ${formatINR(
                      priceDetails?.adjustedTicketCategories?.[0]?.price ?? selectedTier.price
                    )})`}
                    value={formatINR(priceDetails?.cartAmount ?? selectedTier.price * qty)}
                  />
                  <Row label={`Convenience Fees`} value={formatINR(priceDetails?.convenienceFee ?? 0)} />
                  {priceDetails?.discountApplied && appliedCoupon?.code && (
                    <Row
                      label={`Discount (${appliedCoupon?.code})`}
                      value={`− ${formatINR(priceDetails?.discountAmount ?? 0)}`}
                    />
                  )}
                  <Row
                    label={`GST (${((priceDetails?.gstRate ?? 0) * 100) || 0}%)`}
                    value={formatINR(priceDetails?.gstAmount ?? 0)}
                  />
                  <div className="h-px bg-slate-200 my-2" />
                  <RowBig
                    label="Total Payable"
                    value={formatINR(priceDetails?.finalAmount ?? 0)}
                  />
                </div>
                <div className="p-4 pt-0 hidden md:block">
                  <button
                    onClick={() => setShowDetailsModal(true)}
                    className="w-full rounded-full bg-amber-400 px-6 py-2.5 font-semibold text-slate-900 hover:bg-amber-300 disabled:opacity-60"
                    disabled={bookDisabled}
                  >
                    {payoutLoading ? "Processing..." : "Book Show"}
                  </button>
                  {qty > (selectedTier?.availableSeats ?? 0) && (
                    <p className="mt-2 text-xs text-red-600">
                      Quantity exceeds available seats.
                    </p>
                  )}
                </div>
              </div>

              <Link
                to={`/events/${event._id}`}
                className="block text-center text-amber-700 hover:underline text-sm"
              >
                ← Back to Event
              </Link>
            </div>
          </aside>
        </div>
      </main>

      {/* Mobile checkout footer — above bottom nav */}
      {selectedTier && (
        <>
          <div aria-hidden className="h-20 md:hidden" />
          <div className="fixed inset-x-0 bottom-[4.75rem] z-40 border-t border-amber-100/80 bg-white/95 font-sans shadow-[0_-12px_40px_rgba(0,0,0,0.08)] backdrop-blur-md md:hidden">
            <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  {selectedTier.tier} × {qty}
                </div>
                <div className="truncate text-xl font-extrabold tracking-tight text-amber-800">
                  {formatINR(priceDetails?.finalAmount ?? selectedTier.price * qty)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowDetailsModal(true)}
                className="shrink-0 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-slate-900 shadow-sm hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={bookDisabled}
              >
                {payoutLoading ? "Processing..." : "Book Show"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Coupon Applied Animated Popup */}
      {/* {showCouponPopup && (
        <div className="fixed inset-0 z-60 flex items-end md:items-center justify-center pointer-events-none">
          <div
            className={`w-full max-w-sm mx-4 mb-8 md:mb-0 transition-all duration-300 transform ${showCouponPopup ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-6 scale-95"
              } pointer-events-auto`}
          >
            <div
              className="bg-white border rounded-2xl shadow-lg p-4 flex items-start gap-3"
              role="dialog"
              aria-live="polite"
            >
              <div className="flex-shrink-0">
                <div className="h-12 w-12 grid place-items-center rounded-full bg-emerald-100 text-emerald-700 font-bold">
                  ✓
                </div>
              </div>

              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      Coupon applied {couponPopupInfo.code ? `• ${couponPopupInfo.code}` : ""}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      You saved <span className="font-semibold">{formatINR(Number(couponPopupInfo.discountAmount || 0))}</span>
                      {couponPopupInfo.finalAmount ? (
                        <span className="ml-2">• New total {formatINR(Number(couponPopupInfo.finalAmount || 0))}</span>
                      ) : null}
                    </div>
                  </div>

                  <div className="ml-3 flex-shrink-0">
                    <button
                      onClick={() => setShowCouponPopup(false)}
                      className="rounded-full border px-2 py-1 text-xs hover:bg-slate-50"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <div className="mt-3 text-xs text-slate-500">
                  Discount applied to cart. You can continue to payment.
                </div>
              </div>
            </div>
          </div>
        </div>
      )} */}

      {/* === Mobile-first Coupon Popup (replace previous showCouponPopup block) === */}
      {showCouponPopup && (
        <>
          <style>{`
      /* backdrop */
      .cp-backdrop {
        animation: cp-fade-in .18s ease forwards;
      }
      @keyframes cp-fade-in { from { opacity: 0 } to { opacity: 0.2 } }

      /* card pop (mobile-friendly) */
      .cp-card {
        animation: cp-pop .45s cubic-bezier(.2,.9,.2,1) forwards;
        width: 94vw;
        max-width: 520px;
        border-radius: 16px;
      }
      @keyframes cp-pop {
        0% { opacity: 0; transform: translateY(16px) scale(.98); }
        60% { opacity: 1; transform: translateY(-6px) scale(1.02); }
        100% { transform: translateY(0) scale(1); }
      }

      /* small burst behind icon (kept subtle for mobile) */
      .cp-burst {
        position: absolute;
        width: 120px;
        height: 120px;
        border-radius: 50%;
        filter: blur(12px);
        opacity: 0;
        transform: scale(.7);
        animation: cp-burst .5s cubic-bezier(.2,.9,.2,1) forwards;
        pointer-events: none;
      }
      @keyframes cp-burst {
        0% { opacity: 0; transform: scale(.7) }
        45% { opacity: .9; transform: scale(1.08) }
        100% { opacity: 0; transform: scale(1.3) }
      }

      .cp-icon { animation: cp-icon-pop .42s cubic-bezier(.2,.9,.2,1) forwards; }
      @keyframes cp-icon-pop {
        0% { transform: scale(.6); opacity: 0; }
        60% { transform: scale(1.1); opacity: 1; }
        100% { transform: scale(1); }
      }

      /* confetti container - limited height so mobile doesn't overflow */
      .confetti-zone {
        position: absolute;
        left: 6%;
        right: 6%;
        top: 6%;
        height: 34vh;
        pointer-events: none;
        overflow: visible;
      }
      .confetti {
        position: absolute;
        width: 8px;
        height: 12px;
        border-radius: 2px;
        opacity: 0.95;
        transform-origin: center;
        animation: conf-fall linear forwards;
      }
      @keyframes conf-fall {
        0% { transform: translateY(0) rotate(0deg); opacity: 1; }
        100% { transform: translateY(120vh) rotate(540deg); opacity: 0; }
      }

      .cp-amount { animation: cp-pulse 1s ease 0s 2; }
      @keyframes cp-pulse {
        0% { transform: scale(1) }
        50% { transform: scale(1.05) }
        100% { transform: scale(1) }
      }

      /* responsive tweaks */
      @media (min-width: 640px) {
        .cp-card { width: 80vw; }
        .confetti-zone { height: 28vh; left: 20%; right: 20%; }
      }
    `}</style>

          {/* Backdrop */}
          <div
            className="fixed inset-0 z-50 cp-backdrop"
            style={{ background: "rgba(0,0,0,0.36)", pointerEvents: "auto" }}
            onClick={() => setShowCouponPopup(false)}
          />

          {/* Confetti limited zone */}
          <div className="fixed inset-0 z-55 pointer-events-none">
            <div className="confetti-zone">
              {[
                { left: "8%", delay: 0, bg: "#f59e0b", dur: 1200 },
                { left: "22%", delay: 80, bg: "#10b981", dur: 1350 },
                { left: "36%", delay: 160, bg: "#06b6d4", dur: 1100 },
                { left: "50%", delay: 40, bg: "#ef4444", dur: 1400 },
                { left: "64%", delay: 100, bg: "#f97316", dur: 1250 },
                { left: "78%", delay: 60, bg: "#60a5fa", dur: 1500 },
              ].map((c, i) => (
                <div
                  key={i}
                  className="confetti"
                  style={{
                    left: c.left,
                    top: `${-6 - (i % 3) * 4}%`,
                    background: c.bg,
                    animationDuration: `${c.dur}ms`,
                    animationDelay: `${c.delay}ms`,
                  }}
                />
              ))}
            </div>
          </div>

          {/* Centered card (mobile-friendly stacked layout) */}
          <div
            className="fixed inset-0 z-60 flex items-center justify-center pointer-events-none px-4"
            aria-live="assertive"
          >
            <div className="pointer-events-auto">
              <div className="relative cp-card bg-white shadow-2xl border p-4 md:p-6">
                {/* subtle burst behind icon */}
                <div
                  className="cp-burst"
                  style={{
                    left: "14px",
                    top: "-36px",
                    background: "radial-gradient(circle at 30% 30%, rgba(34,197,94,0.28), rgba(59,130,246,0.08) 45%)",
                  }}
                />

                <div className="flex flex-col gap-4 items-center text-center">
                  <div className="relative">
                    <div className="cp-icon h-16 w-16 md:h-20 md:w-20 rounded-full bg-emerald-50 grid place-items-center">
                      <div className="text-2xl md:text-3xl font-extrabold text-emerald-600">✓</div>
                    </div>
                  </div>

                  <div className="min-w-0">
                    <div className="text-sm md:text-base font-semibold text-emerald-700">
                      Coupon applied {couponPopupInfo.code ? `• ${couponPopupInfo.code}` : ""}
                    </div>

                    <div className="mt-2">
                      <div className="text-xl md:text-2xl font-extrabold text-slate-900 cp-amount">
                        {couponPopupInfo.discountAmount
                          ? `You saved ${formatINR(Number(couponPopupInfo.discountAmount || 0))}`
                          : "Discount applied"}
                      </div>

                      {couponPopupInfo.finalAmount ? (
                        <div className="mt-1 text-sm md:text-base text-slate-600">
                          New total: <span className="font-semibold">{formatINR(Number(couponPopupInfo.finalAmount || 0))}</span>
                          <span className="ml-2 text-xs text-slate-400">({couponPopupInfo.cartAmount ? `was ${formatINR(Number(couponPopupInfo.cartAmount || 0))}` : ""})</span>
                        </div>
                      ) : null}

                      <div className="mt-3 text-xs text-slate-500">
                        Discount applied to your cart. Proceed to payment to complete booking.
                      </div>
                    </div>
                  </div>

                  <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                    <button
                      onClick={() => {
                        setShowCouponPopup(false);
                      }}
                      className="w-full rounded-lg border px-4 py-3 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      Close
                    </button>

                    <button
                      onClick={() => {
                        setShowCouponPopup(false);
                        setShowDetailsModal(true);
                      }}
                      className="w-full rounded-lg bg-amber-500 px-4 py-3 text-sm font-semibold text-white hover:bg-amber-600"
                    >
                      Proceed to payment
                    </button>
                  </div>

                  {/* lightweight breakdown */}
                  <div className="mt-2 w-full text-xs text-slate-600 grid grid-cols-2 gap-1">
                    <div>Cart</div>
                    <div className="text-right">{formatINR(Number(couponPopupInfo.cartAmount || priceDetails?.cartAmount || 0))}</div>
                    <div>Discount</div>
                    <div className="text-right">− {formatINR(Number(couponPopupInfo.discountAmount || priceDetails?.discountAmount || 0))}</div>
                    <div className="font-semibold">Payable</div>
                    <div className="text-right font-semibold">{formatINR(Number(couponPopupInfo.finalAmount || priceDetails?.finalAmount || 0))}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}


      {/* T&C Modal */}
      {tncOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setTncOpen(false)} />
          <div className="relative z-10 w-[90vw] max-w-xl max-h-[75vh] overflow-auto rounded-2xl bg-white shadow-xl border">
            <div className="p-4 border-b flex items-center justify-between">
              <h4 className="font-semibold text-slate-900 text-base">{tncTitle}</h4>
              <button
                onClick={() => setTncOpen(false)}
                className="rounded-full border px-2 py-1 text-sm hover:bg-slate-50"
              >
                ✕
              </button>
            </div>
            <div className="p-4 text-sm text-slate-700 leading-6">
              {Array.isArray(tncContent) ? (
                <ul className="list-disc pl-5 space-y-1">
                  {tncContent.map((line, idx) => (
                    <li key={idx}>{line}</li>
                  ))}
                </ul>
              ) : /<[^>]+>/.test(String(tncContent || "")) ? (
                <div dangerouslySetInnerHTML={{ __html: tncContent }} />
              ) : (
                <pre className="whitespace-pre-wrap">{String(tncContent || "")}</pre>
              )}
            </div>
            <div className="p-4 pt-0">
              <button
                onClick={() => setTncOpen(false)}
                className="ml-auto block rounded-full bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-amber-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Seating Layout Modal */}
      {layoutOpen && layoutSrc && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setLayoutOpen(false)} />
          <div
            className="relative z-10 w-[92vw] max-w-5xl max-h-[80vh] overflow-auto rounded-2xl bg-white shadow-xl border p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-slate-900 text-base">
                {event?.name ? `${event.name} — Seating layout` : "Seating layout"}
              </h4>
              <button
                onClick={() => setLayoutOpen(false)}
                className="rounded-full border px-2 py-1 text-sm hover:bg-slate-50"
              >
                ✕
              </button>
            </div>
            <div className="overflow-auto">
              <img
                src={layoutSrc}
                alt="Seating layout"
                className="w-full h-auto rounded-lg border"
                loading="lazy"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Layout is indicative and may vary on the day of the event.
            </p>
          </div>
        </div>
      )}

      {showPopupSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 text-center animate-fadeIn">
            <div className="flex flex-col items-center gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-14 w-14 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>

              <h2 className="text-xl font-semibold text-gray-800">Booking Completed!</h2>
              <p className="text-gray-600 text-sm">
                Your booking has been successfully completed. You can view your Tickets in “My Tickets”.
              </p>

              <div className="mt-3 w-full bg-gray-50 border rounded-lg p-3 text-left text-sm">
                <div className="flex justify-between mb-1">
                  <span className="font-medium text-gray-700">Booking Code:</span>
                  <span className="text-gray-900 font-semibold">
                    {popupBooking?.bookingCode || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Status:</span>
                  <span className={`font-semibold ${popupBooking?.bookingStatus === "Confirmed" ? "text-green-600" : "text-orange-600"}`}>
                    {popupBooking?.bookingStatus || "Pending"}
                  </span>
                </div>
              </div>

              <button
                onClick={() => {
                  setShowPopupSuccess(false);
                  nav("/tickets");
                }}
                className="mt-5 bg-amber-600 text-white px-6 py-2 rounded-md hover:bg-amber-700 transition"
              >
                View My Tickets
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (modalSubmitting) return;
              setShowDetailsModal(false);
            }}
          />
          <div className="relative z-10 w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h3 className="mb-4 text-lg font-semibold">Confirm your details</h3>

            <label className="block mb-2 text-sm">
              Name
              <input
                type="text"
                value={details.name}
                onChange={(e) => setDetails((s) => ({ ...s, name: e.target.value }))}
                className="mt-1 w-full rounded border px-3 py-2"
                placeholder="Full name"
              />
            </label>

            <label className="block mb-2 text-sm">
              Email <span className="text-slate-400">(optional)</span>
              <input
                type="email"
                value={details.email}
                onChange={(e) => setDetails((s) => ({ ...s, email: e.target.value }))}
                className="mt-1 w-full rounded border px-3 py-2"
                placeholder="email@example.com"
              />
            </label>

            <label className="block mb-4 text-sm">
              Mobile (cannot be changed)
              <input
                type="tel"
                inputMode="numeric"
                value={details.mobile}
                readOnly
                disabled
                className="mt-1 w-full rounded border bg-gray-100 px-3 py-2 text-gray-700"
              />
            </label>

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDetailsModal(false)}
                className="rounded px-4 py-2 text-sm"
                disabled={modalSubmitting}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={async () => {
                  if (!details.name?.trim()) {
                    toast.error("Please enter name");
                    return;
                  }
                  if (details.email?.trim() && !isEmail(details.email)) {
                    toast.error("Please enter a valid email or leave it empty");
                    return;
                  }
                  if (!toTenDigitMobile(details.mobile)) {
                    toast.error("Mobile number missing. Please log in again.");
                    return;
                  }
                  setModalSubmitting(true);
                  await handlePayoutClick(details);
                  setModalSubmitting(false);
                  setShowDetailsModal(false);
                }}
                className="rounded-full bg-amber-400 px-4 py-2 text-sm font-semibold hover:bg-amber-300 disabled:opacity-60"
                disabled={modalSubmitting}
              >
                {modalSubmitting ? "Please wait..." : "Confirm & Proceed"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* Helpers */
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-slate-600">{label}</div>
      <div className="font-medium text-slate-900">{value}</div>
    </div>
  );
}
function RowBig({ label, value }) {
  return (
    <div className="flex items-center justify-between text-base">
      <div className="font-semibold text-slate-900">{label}</div>
      <div className="font-extrabold text-slate-900">{value}</div>
    </div>
  );
}
